import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadFilledForm, workerLogout } from '../api';
import './UploadFilledForm.css';

/**
 * Upload Filled Form Component
 * Allows workers to upload completed Word/Excel/PDF documents
 * REQUIRES AUTHENTICATION
 */
function UploadFilledForm({ user, onLogout }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    worker_name: user?.full_name || '',
    job_site: '',
    submission_date: new Date().toISOString().split('T')[0],
    form_type: '',
    comments: ''
  });

  // Update worker_name if user changes
  useEffect(() => {
    if (user?.full_name) {
      setFormData(prev => ({
        ...prev,
        worker_name: user.full_name
      }));
    }
  }, [user]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isDragging, setIsDragging] = useState(false);

  // Available form types (matching the documents)
  const formTypes = [
    { id: 'concrete-safety', label: 'Concrete Safety Daily Log' },
    { id: 'facade', label: 'Facade Daily Log' },
    { id: 'fire-safety', label: 'Fire Safety Manager Daily Log' },
    { id: 'site-coordinator', label: 'Site Safety Coordinator Daily Log' },
    { id: 'site-manager', label: 'Site Safety Manager Daily Log' },
    { id: 'superintendent', label: 'Superintendent Daily Log' },
    { id: 'timesheet', label: 'Weekly TimeSheet' },
    { id: 'other', label: 'Other' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogout = async () => {
    try {
      await workerLogout();
      localStorage.removeItem('worker_user');
      if (onLogout) {
        onLogout();
      }
      navigate('/worker/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const validateFile = (selectedFile) => {
    // Validate file size (50MB max per file)
    if (selectedFile.size > 52428800) {
      return {
        valid: false,
        error: `${selectedFile.name}: File size exceeds 50MB limit`
      };
    }

    // Validate file type
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/msword', // .doc
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'application/pdf', // .pdf
      'image/jpeg', // .jpg, .jpeg
      'image/png', // .png
      'image/gif' // .gif
    ];

    if (!allowedTypes.includes(selectedFile.type)) {
      return {
        valid: false,
        error: `${selectedFile.name}: Invalid file format. Only Word, Excel, PDF, and Image files are allowed.`
      };
    }

    return { valid: true };
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    if (selectedFiles.length === 0) {
      return;
    }

    // Validate all files
    const validFiles = [];
    const errors = [];

    selectedFiles.forEach(file => {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        errors.push(validation.error);
      }
    });

    if (errors.length > 0) {
      setMessage({
        type: 'error',
        text: errors.join(', ')
      });
    } else {
      setMessage({ type: '', text: '' });
    }

    // Check total submission size (500MB max)
    if (validFiles.length > 0) {
      const currentTotalSize = files.reduce((sum, file) => sum + file.size, 0);
      const newFilesSize = validFiles.reduce((sum, file) => sum + file.size, 0);
      const totalSize = currentTotalSize + newFilesSize;
      const maxTotalSize = 524288000; // 500MB

      if (totalSize > maxTotalSize) {
        setMessage({
          type: 'error',
          text: `Total submission size exceeds 500MB limit. Current: ${(currentTotalSize / 1048576).toFixed(2)}MB, Adding: ${(newFilesSize / 1048576).toFixed(2)}MB`
        });
        return;
      }

      // Add valid files to existing files
      setFiles(prev => [...prev, ...validFiles]);
    }

    // Clear input
    e.target.value = '';
  };

  const removeFile = (indexToRemove) => {
    setFiles(prev => prev.filter((_, index) => index !== indexToRemove));
    setMessage({ type: '', text: '' });
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);

    if (droppedFiles.length === 0) {
      return;
    }

    // Validate all files
    const validFiles = [];
    const errors = [];

    droppedFiles.forEach(file => {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        errors.push(validation.error);
      }
    });

    if (errors.length > 0) {
      setMessage({
        type: 'error',
        text: errors.join(', ')
      });
    } else {
      setMessage({ type: '', text: '' });
    }

    // Check total submission size (500MB max)
    if (validFiles.length > 0) {
      const currentTotalSize = files.reduce((sum, file) => sum + file.size, 0);
      const newFilesSize = validFiles.reduce((sum, file) => sum + file.size, 0);
      const totalSize = currentTotalSize + newFilesSize;
      const maxTotalSize = 524288000; // 500MB

      if (totalSize > maxTotalSize) {
        setMessage({
          type: 'error',
          text: `Total submission size exceeds 500MB limit. Current: ${(currentTotalSize / 1048576).toFixed(2)}MB, Adding: ${(newFilesSize / 1048576).toFixed(2)}MB`
        });
        return;
      }

      // Add valid files to existing files
      setFiles(prev => [...prev, ...validFiles]);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.worker_name.trim()) {
      setMessage({ type: 'error', text: 'Please enter your name' });
      return;
    }

    if (!formData.job_site.trim()) {
      setMessage({ type: 'error', text: 'Please enter job site' });
      return;
    }

    if (!formData.form_type) {
      setMessage({ type: 'error', text: 'Please select form type' });
      return;
    }

    if (files.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one file to upload' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const submitData = new FormData();
      submitData.append('worker_name', formData.worker_name);
      submitData.append('job_site', formData.job_site);
      submitData.append('submission_date', formData.submission_date);
      submitData.append('form_type', formData.form_type);
      submitData.append('comments', formData.comments);

      // Append all files
      files.forEach((file, index) => {
        submitData.append(`filled_form_${index}`, file);
      });
      submitData.append('file_count', files.length);

      // Call upload API endpoint
      const response = await uploadFilledForm(submitData);

      if (response.success) {
        setMessage({
          type: 'success',
          text: `✓ Form uploaded successfully with ${files.length} file(s)!`
        });

        // Reset form (preserve worker_name from user)
        setFormData({
          worker_name: user?.full_name || '',
          job_site: '',
          submission_date: new Date().toISOString().split('T')[0],
          form_type: '',
          comments: ''
        });
        setFiles([]);
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';

        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/worker/dashboard');
        }, 2000);
      }

    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || 'Upload failed. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="upload-form-page">
      {/* Header */}
      <header className="upload-header">
        <div className="upload-header-container">
          <div className="brand">
            <img src="/logo-full.png" alt="Skilled Safety Management" className="logo-full" />
          </div>
          <div className="header-actions">
            <span className="user-greeting">Hello, {user?.full_name || 'Worker'}</span>
            <button onClick={() => navigate('/worker/dashboard')} className="btn-dashboard">
              📊 Dashboard
            </button>
            <button onClick={handleLogout} className="btn-logout">
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="upload-main">
        <div className="upload-container">
          {/* Page Title */}
          <div className="upload-title-section">
            <h1 className="upload-title">📤 Upload Completed Form</h1>
            <p className="upload-subtitle">
              Upload your completed Word, Excel, PDF, or Image document
            </p>
          </div>

          {/* Alert Message */}
          {message.text && (
            <div className={`alert alert-${message.type}`}>
              <div className="alert-content">
                {message.type === 'success' ? '✓' : '✕'} {message.text}
              </div>
              <button
                className="alert-close"
                onClick={() => setMessage({ type: '', text: '' })}
              >
                ×
              </button>
            </div>
          )}

          {/* Upload Form */}
          <form onSubmit={handleSubmit} className="upload-form">
            <div className="form-grid">
              {/* Worker Name */}
              <div className="form-group">
                <label htmlFor="worker_name" className="form-label">
                  Your Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="worker_name"
                  name="worker_name"
                  value={formData.worker_name}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Enter your full name"
                  required
                />
              </div>

              {/* Job Site */}
              <div className="form-group">
                <label htmlFor="job_site" className="form-label">
                  Job Site <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="job_site"
                  name="job_site"
                  value={formData.job_site}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Enter job site location"
                  required
                />
              </div>

              {/* Date */}
              <div className="form-group">
                <label htmlFor="submission_date" className="form-label">
                  Date <span className="required">*</span>
                </label>
                <input
                  type="date"
                  id="submission_date"
                  name="submission_date"
                  value={formData.submission_date}
                  onChange={handleInputChange}
                  className="form-input"
                  max={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              {/* Form Type */}
              <div className="form-group">
                <label htmlFor="form_type" className="form-label">
                  Form Type <span className="required">*</span>
                </label>
                <select
                  id="form_type"
                  name="form_type"
                  value={formData.form_type}
                  onChange={handleInputChange}
                  className="form-select"
                  required
                >
                  <option value="">-- Select Form Type --</option>
                  {formTypes.map(type => (
                    <option key={type.id} value={type.label}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* File Upload */}
              <div className="form-group full-width">
                <label htmlFor="file_upload" className="form-label">
                  Upload Filled Forms <span className="required">*</span>
                  {files.length > 0 && <span className="file-count"> ({files.length} file{files.length > 1 ? 's' : ''} selected)</span>}
                </label>
                <div
                  className={`file-upload-area ${isDragging ? 'dragging' : ''}`}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    id="file_upload"
                    className="file-input-hidden"
                    onChange={handleFileChange}
                    accept=".doc,.docx,.xls,.xlsx,.pdf,.jpg,.jpeg,.png,.gif"
                    multiple
                  />
                  <label htmlFor="file_upload" className="file-upload-label">
                    <div className="upload-icon">📁</div>
                    <div className="upload-text">
                      <span className="upload-title">
                        {isDragging ? 'Drop files here' : 'Click to select or drag & drop files'}
                      </span>
                      <span className="upload-subtitle">
                        Word, Excel, PDF, or Image files (JPG, PNG, GIF) - max 10MB per file
                      </span>
                      <span className="upload-subtitle">
                        You can select multiple files at once
                      </span>
                    </div>
                  </label>
                </div>

                {/* Display Selected Files */}
                {files.length > 0 && (
                  <div className="selected-files-list">
                    {files.map((file, index) => (
                      <div key={index} className="selected-file-item">
                        <div className="file-item-icon">
                          {file.type.startsWith('image/') ? '🖼️' :
                           file.type.includes('pdf') ? '📄' :
                           file.type.includes('word') ? '📝' :
                           file.type.includes('sheet') || file.type.includes('excel') ? '📊' : '📎'}
                        </div>
                        <div className="file-item-info">
                          <div className="file-item-name">{file.name}</div>
                          <div className="file-item-size">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </div>
                        </div>
                        <button
                          type="button"
                          className="file-item-remove"
                          onClick={() => removeFile(index)}
                          title="Remove file"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Comments */}
              <div className="form-group full-width">
                <label htmlFor="comments" className="form-label">
                  Comments (Optional)
                </label>
                <textarea
                  id="comments"
                  name="comments"
                  value={formData.comments}
                  onChange={handleInputChange}
                  className="form-textarea"
                  placeholder="Add any additional notes or comments"
                  rows="4"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="form-actions">
              <button
                type="submit"
                className="btn-submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span>Uploading</span>
                    <div className="btn-loading-dots">
                      <span className="dot"></span>
                      <span className="dot"></span>
                      <span className="dot"></span>
                    </div>
                  </>
                ) : (
                  <>
                    <svg className="btn-icon-submit" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                    <span>Upload & Submit</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>

      {/* Footer */}
      <footer className="upload-footer">
        <div className="footer-content">
          <div className="footer-info">
            <p>📞 Need help? Call us at <a href="tel:+12124988863">+1 (212)-498-8863</a></p>
            <p>© 2025 Skilled Safety Management. All Rights Reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default UploadFilledForm;
